import { PlatformBrand } from '@/components/passport/PlatformBrand';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import { usePassportStore } from '@/stores/passport-store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import type {
  PassportScanOutput as PassportScanOutputType,
  PassportSessionInfoDto as PassportSessionInfoDtoType,
} from '@tunodo/ticketing-shared';
import { PassportScanOutput, PassportSessionInfoDto } from '@tunodo/ticketing-shared';
import { BookOpen, CheckCircle2, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/passport/stamp')({
  validateSearch: z.object({ token: z.string().uuid() }),
  beforeLoad: ({ search }) => {
    if (!search.token) {
      const { user } = usePassportStore.getState();
      throw redirect({ to: user ? '/passport/events' : '/passport' });
    }
  },
  component: PassportStampPage,
});

type ScanResult = PassportScanOutputType['result'] | 'error';

function PassportStampPage() {
  const { token } = Route.useSearch();
  const user = usePassportStore((s) => s.user);
  const setPendingStampToken = usePassportStore((s) => s.setPendingStampToken);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [scannedActivity, setScannedActivity] = useState<PassportScanOutputType['activity']>(null);
  const [isScanning, setIsScanning] = useState(false);
  const firedRef = useRef(false);

  const { data: sessionInfo, isLoading: isSessionLoading } = useQuery<PassportSessionInfoDtoType>({
    queryKey: ['passport', 'session-info', token],
    queryFn: () =>
      apiFetch(`/api/passport/session-info/${token}`, { schema: PassportSessionInfoDto }),
  });

  useEffect(() => {
    if (!user || firedRef.current || !token) return;
    firedRef.current = true;

    setPendingStampToken(null);
    setIsScanning(true);

    apiFetch<PassportScanOutputType>('/api/passport/scan', {
      method: 'POST',
      body: JSON.stringify({ userId: user.userId, token }),
      schema: PassportScanOutput,
    })
      .then((result) => {
        setScanResult(result.result);
        setScannedActivity(result.activity);
        void queryClient.invalidateQueries({
          queryKey: ['passport', user.userId, 'events', sessionInfo?.eventId],
        });
      })
      .catch(() => {
        setScanResult('error');
      })
      .finally(() => {
        setIsScanning(false);
        setPendingStampToken(null);
      });
  }, [user, token, setPendingStampToken, queryClient, sessionInfo?.eventId]);

  if (!user) {
    return (
      <div className="relative flex min-h-screen flex-col items-center justify-center gap-6 bg-background p-6 text-center">
        <PlatformBrand className="absolute top-4 left-1/2 -translate-x-1/2" />
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: '#522B9A' }}
        >
          <BookOpen className="h-8 w-8 text-white" />
        </div>
        {isSessionLoading ? (
          <p className="text-sm text-muted-foreground">Cargando información de la ponencia…</p>
        ) : sessionInfo ? (
          <>
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-bold">{sessionInfo.title}</h2>
              {sessionInfo.speakerName && (
                <p className="text-sm text-muted-foreground">{sessionInfo.speakerName}</p>
              )}
              <p className="text-xs text-muted-foreground">{sessionInfo.eventTitle}</p>
            </div>
            <p className="text-sm text-gray-600 max-w-xs">
              Inicia sesión con tu email para sellar esta ponencia en tu pasaporte.
            </p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Ponencia no encontrada.</p>
        )}
        <Button
          className="text-white w-full max-w-xs"
          style={{ backgroundColor: '#522B9A' }}
          onClick={() => {
            setPendingStampToken(token);
            navigate({ to: '/passport' });
          }}
        >
          Iniciar sesión para sellar
        </Button>
      </div>
    );
  }

  if (isScanning || scanResult === null) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6">
        <p className="text-lg font-medium text-muted-foreground">Procesando…</p>
      </div>
    );
  }

  if (scanResult === 'OK') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center">
        <CheckCircle2 className="h-24 w-24 text-green-500 animate-stamp" />
        <p className="text-2xl font-bold text-green-700">¡Sello obtenido!</p>
        {scannedActivity && (
          <>
            <p className="text-base font-medium text-gray-800">{scannedActivity.title}</p>
            {scannedActivity.speakerName && (
              <p className="text-sm text-gray-600">{scannedActivity.speakerName}</p>
            )}
          </>
        )}
        <Button
          className="text-white mt-2"
          style={{ backgroundColor: '#522B9A' }}
          onClick={() =>
            navigate({
              to: '/passport/events/$eventId',
              params: { eventId: sessionInfo?.eventId ?? '' },
            })
          }
        >
          Ver mi pasaporte
        </Button>
      </div>
    );
  }

  if (scanResult === 'DUPLICATE') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center">
        <XCircle className="h-24 w-24 text-yellow-500" />
        <p className="text-2xl font-bold text-yellow-700">Ya tienes este sello</p>
        {scannedActivity && <p className="text-sm text-gray-600">{scannedActivity.title}</p>}
        <Button
          className="text-white mt-2"
          style={{ backgroundColor: '#522B9A' }}
          onClick={() =>
            navigate({
              to: '/passport/events/$eventId',
              params: { eventId: sessionInfo?.eventId ?? '' },
            })
          }
        >
          Ver mi pasaporte
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center">
      <XCircle className="h-24 w-24 text-red-500" />
      <p className="text-2xl font-bold text-red-700">QR no reconocido o expirado</p>
      <Button variant="outline" onClick={() => navigate({ to: '/passport/events' })}>
        Volver
      </Button>
    </div>
  );
}
